<template>
    <div>
        <header
            class="flex items-center px-4 py-2 font-bold text-gray-700 bg-gray-100 border border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
        >
            <div class="flex-auto">
                {{ entry.name }}
            </div>
            <div class="leading-none grow-0">
                <Button v-if="showAdvanced" color="brand" size="sm" @click="showCreating(entry)">
                    <icon icon="hero/outline/plus-circle" class="w-4 h-4"></icon>
                </Button>
            </div>
        </header>

        <create-slot
            v-if="creating !== false"
            :reservation-slot="creating"
            :reservables="reservables"
            :fields="creatingFields"
            :show-advanced="showAdvanced"
            :calculated-name="calculatedName"
            :mass-create="massCreate"
            @close="closeCreating"
            @created="created"
        ></create-slot>

        <book-slot
            v-if="booking !== false"
            :reservation-slot="booking"
            :metas="metas"
            :show-advanced="showAdvanced"
            :select-color="selectColor"
            :mass-create="massCreate"
            @close="booking = false"
            @booked="booked"
        ></book-slot>

        <show-slot
            v-if="showing !== false"
            :reservation-slot="showing"
            :widgets="showingWidgets"
            :select-color="selectColor"
            :mass-create="massCreate"
            :metas="metas"
            @updated="updated"
            @deleted="deleted"
            @close="showing = false"
        ></show-slot>

        <generate-slots
            v-if="generatingSlots !== false"
            :day="generatingSlots"
            @close="generatingSlots = false"
            @generated="generated"
        />

        <main
            class="bg-gray-50 dark:bg-zinc-900"
            :class="[
                entry.children && entry.children.length > 0
                    ? ''
                    : 'divide-x divide-gray-300 border border-gray-300 dark:border-zinc-700 dark:divide-zinc-700',
                `grid grid-cols-1 lg:grid-cols-7`,
                loading.groups ? 'animate-pulse' : '',
            ]"
        >
            <div
                v-for="group in groups"
                :key="group.date"
                class="flex flex-col flex-auto text-xs bg-white hover:bg-gray-100 dark:bg-zinc-900 dark:hover:bg-zinc-950"
                :class="[`lg:col-span-1`]"
                @contextmenu.prevent.stop="contextmenu({ group, event: $event })"
            >
                <header
                    v-if="showAdvanced"
                    class="relative z-10 flex items-center justify-between px-2 py-1 bg-gray-100 border-b border-gray-300 shadow dark:bg-zinc-800 dark:border-zinc-700"
                >
                    <div class="flex items-center w-1/3 space-x-2">
                        <Button
                            v-tooltip="{
                                message: lang('reservations::schedule.actions.create_multiple_slots'),
                                position: 'top-left',
                            }"
                            size="xs"
                            @click="showGenerating(group)"
                        >
                            <span>
                                <icon icon="hero/outline/plus" class="w-3 h-3" />
                                <icon icon="hero/outline/plus" class="w-3 h-3 -mt-1 -ml-1" />
                            </span>
                        </Button>
                        <Button
                            v-tooltip="{
                                message: lang('reservations::schedule.actions.create_slot'),
                                position: 'top-left',
                            }"
                            size="xs"
                            @click="addSingleSlot(group)"
                        >
                            <span>
                                <icon icon="hero/outline/plus" class="w-3 h-3" />
                            </span>
                        </Button>
                    </div>
                    <div class="flex-1 w-1/3 text-center"></div>
                    <div class="flex items-center justify-end w-1/3">
                        <Button
                            v-show="group.slots.length"
                            v-tooltip="{
                                message: lang('reservations::schedule.actions.delete_slots'),
                                position: 'top-left',
                            }"
                            size="xs"
                            @click="deleteAllSlots(group)"
                        >
                            <span>
                                <icon icon="hero/outline/minus" class="w-3 h-3" />
                            </span>
                        </Button>
                    </div>
                </header>
                <div
                    v-tooltip="{ message: group.date, position: 'top-left' }"
                    class="lg:text-right py-0.5 px-2 text-lg font-medium"
                >
                    {{ group.weekday }}
                    <span class="text-xs lg:hidden">{{ group.date }}</span>
                </div>
                <main class="flex-auto border-t border-gray-200 dark:border-zinc-800">
                    <div class="grid grid-cols-4 gap-px">
                        <!-- Empty group -->
                        <div v-show="isEmptyGroup(group)" class="col-span-4 py-10 space-y-2 text-center">
                            <p>
                                {{ lang('reservations::schedule.status.empty_day') }}
                            </p>

                            <Button
                                v-if="!massCreate"
                                v-tooltip="{
                                    message: lang('reservations::schedule.actions.create_multiple_slots'),
                                    position: 'top',
                                }"
                                size="xs"
                                @click="showGenerating(group)"
                            >
                                <span>
                                    <icon icon="hero/outline/circle-stack" class="w-3 h-3"></icon>
                                </span>
                            </Button>
                            <Button
                                v-else
                                v-tooltip="{
                                    message: lang('reservations::schedule.actions.create_multiple_slots'),
                                    position: 'top',
                                }"
                                size="xs"
                                @click="
                                    addSingleSlot(
                                        group,
                                        group.nextSlotName,
                                        group.nextSlotTimeFromISO,
                                        group.nextSlotTimeToISO,
                                    )
                                "
                            >
                                <span>
                                    <icon icon="hero/outline/circle-stack" class="w-3 h-3"></icon>
                                </span>
                            </Button>
                        </div>
                        <!-- Slots -->
                        <template v-if="!showAdvanced">
                            <div
                                v-for="slot in group.slots"
                                :key="slot.id"
                                class="text-center py-1.5 lg:py-0.5 cursor-pointer select-none"
                                :class="[
                                    {
                                        'bg-green-200 hover:bg-green-100 shadow-inner dark:text-black':
                                            contextItem && isContextSelected(slot),
                                    },
                                    slot.reservation && !isContextSelected(slot)
                                        ? `bg-${slot.color}-400 hover:bg-${slot.color}-300 shadow-inner dark:text-black`
                                        : '',
                                    !slot.reservation && !isContextSelected(slot)
                                        ? 'bg-white dark:bg-zinc-950 ring-brand-500 hover:ring-2 active:ring-4 hover:z-20 active:z-20 active:outline-hidden hover:bg-brand-600 hover:text-white relative text-gray-700 dark:text-zinc-300'
                                        : '',
                                ]"
                                @click="!slot.reservation ? (booking = slot) : showReservation(slot)"
                                @contextmenu.prevent.stop="contextmenu({ slot, event: $event })"
                            >
                                {{ slot.data.time }}
                            </div>
                        </template>
                        <template v-else-if="showAdvanced && !isEmptyGroup(group)">
                            <div v-for="subgroup in getSubGroups(group.slots)" :key="subgroup.name" class="col-span-4">
                                <div class="py-1 bg-white border-y dark:bg-zinc-950 dark:border-zinc-800">
                                    <span v-if="subgroup && subgroup.name && subgroup.name !== 'undefined'">
                                        {{ subgroup.name }}
                                    </span>
                                    <span v-else>
                                        {{ subgroup.slots[0].reservable.name }}
                                    </span>
                                </div>
                                <div class="grid grid-cols-4 gap-px">
                                    <div
                                        v-for="slot in subgroup.slots"
                                        :key="slot.id"
                                        class="text-center py-1.5 lg:py-0.5 cursor-pointer select-none"
                                        :class="[
                                            {
                                                'bg-green-200 hover:bg-green-100 shadow-inner dark:text-black':
                                                    contextItem && isContextSelected(slot),
                                            },
                                            slot.reservation && !isContextSelected(slot)
                                                ? `bg-${slot.color}-400 hover:bg-${slot.color}-300 shadow-inner dark:text-black`
                                                : '',
                                            !slot.reservation && !isContextSelected(slot)
                                                ? 'bg-white dark:bg-zinc-950 ring-brand-500 hover:ring-2 active:ring-4 hover:z-20 active:z-20 active:outline-hidden hover:bg-brand-600 hover:text-white relative text-gray-700 dark:text-zinc-300'
                                                : '',
                                        ]"
                                        @click="!slot.reservation ? (booking = slot) : showReservation(slot)"
                                        @contextmenu.prevent.stop="contextmenu({ slot, event: $event })"
                                    >
                                        {{ slot.data.time }}
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div
                            v-show="!isEmptyGroup(group)"
                            v-tooltip="{
                                message: lang('reservations::schedule.actions.create_slot'),
                                position: 'top',
                            }"
                            class="flex items-center justify-center bg-transparent shadow-inner cursor-pointer select-none hover:bg-green-700 dark:text-white hover:text-white"
                            @click="
                                addSingleSlot(
                                    group,
                                    group.nextSlotName,
                                    group.nextSlotTimeFromISO,
                                    group.nextSlotTimeToISO,
                                )
                            "
                        >
                            <icon icon="hero/outline/plus-circle" class="w-4 h-4"></icon>
                        </div>
                    </div>
                </main>
                <footer class="relative hidden lg:block">
                    <div class="absolute text-2xl bottom-1 right-2 opacity-20">
                        {{ group.day }}
                    </div>
                </footer>
            </div>
        </main>
    </div>

    <!-- Delete confirmation -->
    <Confirmation :show="deletingSlots.show" @close="deletingSlots.show = true">
        <template #title>Opravdu chcete smazat všechny sloty?</template>
        <template #content>
            <p class="font-bold">{{ deletingSlots.day.weekday }} {{ deletingSlots.day.date }}</p>
            <p>
                <span v-if="deletingSlots.day.slots">{{ deletingSlots.day.slots.length }} </span>
                <span v-else>0</span>
                slotů
            </p>
        </template>
        <template #footer>
            <Button @click="deletingSlots.show = false">
                {{ lang('general.cancel') }}
            </Button>

            <Button color="danger" @click="confirmDeleteAllSlots">
                {{ lang('general.datagrid.delete') }}
            </Button>
        </template>
    </Confirmation>

    <!-- Context menu -->
    <UiContextMenu
        :items="contextMenuItems"
        :x="contextmenuPosition.x"
        :y="contextmenuPosition.y"
        :show="contextmenuShow"
        @close="closeContextmenu"
    />
</template>

<script setup lang="ts">
import axios from 'axios';
import type { Ref } from 'vue';
import { toRef, watch, ref, computed } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { DateTime } from 'luxon';
import { lang } from '@/lang/composable';
import BookSlot from '@src/Reservations/Pages/Schedule/BookSlot.vue';
import CreateSlot from '@src/Reservations/Pages/Schedule/CreateSlot.vue';
import ShowSlot from '@src/Reservations/Pages/Schedule/ShowSlot.vue';
import GenerateSlots from '@src/Reservations/Pages/Schedule/GenerateSlots.vue';
import type { ContextMenuItem } from '@src/Common/Components/UI/ContextMenu/ContextMenu.vue';

interface Props {
    entry?: any;
    schedule?: {
        from: string | null;
        to: string | null;
        group: string;
    };
    metas?: {
        [key: string]: string;
    } | null;
    groups?: any[] | object;
    showAdvanced?: boolean;
    selectColor?: boolean;
    massCreate?: boolean;
    calculatedName?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    entry: {},
    schedule: () => ({
        from: null,
        to: null,
        group: 'day',
    }),
    metas: null,
    groups: () => [],
    showAdvanced: false,
    selectColor: false,
    massCreate: false,
});

const emit = defineEmits(['booked', 'dialog:open', 'dialog:close']);

const entry = toRef(props, 'entry');
const metas = toRef(props, 'metas');
const groups = ref([]);
const deletingSlots = ref({
    show: false,
    day: {},
});
const showAdvanced = toRef(props, 'showAdvanced');
const calculatedName = toRef(props, 'calculatedName');

const loading = ref({
    groups: false,
});

const getData = () => {
    if (!entry.value.id) {
        return;
    }

    if (!props.schedule.from || !props.schedule.to) {
        return;
    }

    loading.value.groups = true;

    axios
        .get(route('reservations/slots.admin.schedule.data'), {
            params: {
                reservable_id: entry.value.id,
                from: DateTime.fromISO(props.schedule.from).toFormat('yyyy-MM-dd'),
                to: DateTime.fromISO(props.schedule.to).toFormat('yyyy-MM-dd'),
                group: props.schedule.group,
            },
        })
        .then(({ data }) => {
            if (data.groups) {
                groups.value = data.groups;
            }
        })
        .finally(() => {
            loading.value.groups = false;
        });
};

watch(props.schedule, getData, { immediate: true });

/**
 * Booking
 */
const booking = ref(false);

const booked = () => {
    booking.value = false;
    emit('booked');
    getData();
};

/**
 * Show slot
 */
const showing = ref(false);
const showingWidgets = ref<Record<string, any[]>>({});

const showReservation = (slot: any) => {
    axios.get(route('reservations/slots.admin.schedule.slot.data', { id: slot.id })).then(({ data }) => {
        showing.value = data.slot;
        showingWidgets.value = data.widgets ?? {};
    });
};

/**
 * Create slot
 */
const creating: Ref<boolean | object> = ref(false);
const reservables = ref([]);
const creatingFields = ref([]);

const showCreating = (entry: any) => {
    axios.get(route('reservations.slots.create.data', { date: entry.date })).then(({ data }) => {
        reservables.value = data.reservables;

        creatingFields.value = data.fields;

        creating.value = {
            reservable_id: entry.id,
            reservable_type: data.reservable_type,
            name: entry.name ?? null,
            time_slot_from: entry.time_slot_from ?? data.times.from,
            time_slot_to: entry.time_slot_to ?? data.times.to,
        };

        emit('dialog:open', 'create-slot');
    });
};

const closeCreating = () => {
    creating.value = false;

    emit('dialog:close', 'create-slot');
};

const created = () => {
    closeCreating();
    getData();
};

const updated = () => {
    getData();
};

const deleted = () => {
    getData();
};

const addSingleSlot = (day: any, nextSlotName?: any, nextSlotTimeFromISO?: any, nextSlotTimeToISO?: any) => {
    const date = day.dateISO;

    showCreating({
        date,
        id: day.reservable_id,
        name: nextSlotName ?? null,
        time_slot_from: nextSlotTimeFromISO ?? null,
        time_slot_to: nextSlotTimeToISO ?? null,
    });
};

const deleteAllSlots = (day) => {
    deletingSlots.value.day = day;
    deletingSlots.value.show = true;
};

const confirmDeleteAllSlots = () => {
    const day = deletingSlots.value.day;
    const id = day.reservable_id;
    const date = day.dateISO;

    const deleteForm = useForm({
        id,
        date,
    });

    deleteForm.delete(route('reservations.slots.delete.all', { id, date }), {
        preserveScroll: true,
        preserveState: true,
        forceFormData: true,
        onSuccess: () => {
            deletingSlots.value = { show: false, day: {} };
            getData();
        },
        onStart: () => ({}),
        onFinish: () => ({}),
    });
};

const contextmenuShow = ref(false);
const contextmenuPosition = ref({ x: 0, y: 0 });
const contextItem = ref(null);

const contextMenuItems = computed<ContextMenuItem[]>(() => {
    if (!contextItem.value) return [];

    const items: ContextMenuItem[] = [];

    // Group context menu items
    if (contextItem.value.interactive_type === 'group') {
        if (!props.massCreate) {
            items.push({
                label: lang('reservations::schedule.actions.create_multiple_slots'),
                icon: 'hero/outline/circle-stack',
                action: () => showGenerating(contextItem.value),
            });
        }

        items.push({
            label: lang('reservations::schedule.actions.create_slot'),
            icon: 'hero/outline/plus-circle',
            action: () => addSingleSlot(contextItem.value),
        });

        items.push({
            label: lang('reservations::schedule.actions.delete_slots'),
            icon: 'hero/outline/trash',
            action: () => deleteAllSlots(contextItem.value),
            danger: true,
        });
    }

    // Slot context menu items
    if (contextItem.value.interactive_type === 'slot') {
        // Book slot (if no reservation)
        if (!contextItem.value.reservation) {
            items.push({
                label: lang('reservations::schedule.actions.book'),
                icon: 'hero/outline/bookmark',
                action: () => (booking.value = contextItem.value),
            });
        }

        // View slot (if has reservation)
        if (contextItem.value.reservation?.id) {
            items.push({
                label: lang('reservations::schedule.actions.view_slot'),
                icon: 'hero/outline/eye',
                action: () => showReservation(contextItem.value),
            });

            items.push({
                label: lang('reservations::schedule.actions.delete_reservation'),
                icon: 'hero/outline/bookmark-slash',
                action: () => deleteReservation(contextItem.value.reservation.id),
                danger: true,
            });
        }

        // Delete slot (always available for slots)
        items.push({
            label: lang('reservations::schedule.actions.delete_slot'),
            icon: 'hero/outline/trash',
            action: deleteSlot,
            danger: true,
        });
    }

    return items;
});

const contextmenu = ({ slot = null, group = null, event }) => {
    if (group) {
        contextItem.value = group;

        contextItem.value.interactive_type = 'group';
    } else if (slot) {
        contextItem.value = slot;

        contextItem.value.interactive_type = 'slot';
    }
    contextmenuShow.value = true;

    contextmenuPosition.value = {
        x: event.clientX,
        y: event.clientY,
    };
};

const closeContextmenu = () => {
    contextmenuShow.value = false;
    contextItem.value = null;
};

const isContextSelected = (slot) => {
    return contextItem.value && contextItem.value.id === slot.id;
};

const deleteSlot = () => {
    axios.delete(route('reservations.slots.delete.single', { id: contextItem.value.id })).then(() => {
        getData();
    });
};

/**
 * Generate slots
 */
const generatingSlots: Ref<boolean | object> = ref(false);

const showGenerating = (day) => {
    axios.get(route('reservations.slots.create.data', { date: day.dateISO })).then(({ data }) => {
        generatingSlots.value = {
            reservable_id: day.reservable_id,
            reservable_type: data.reservable_type,
            date: day.dateISO,
        };
        emit('dialog:open', 'generate-slots');
    });
};

const generated = () => {
    getData();
    generatingSlots.value = false;
};

window.Echo.channel('admin').listen('.reservation.created', () => {
    getData();
});

/**
 * Statuses
 */
const isEmptyGroup = (group): boolean => group.slots.length < 1;

const deleteReservation = (id) => {
    axios.delete(route('reservations.delete', { id })).then(() => {
        deleted();
    });
};

/**
 * Subgroups
 */
const getSubGroups = (slots) => {
    // Group slots into arrays by property "name" and return them as array of objects with name and slots properties
    const groups = slots.reduce((acc, slot) => {
        const name = slot.name;

        if (!acc[name]) {
            acc[name] = [];
        }

        acc[name].push(slot);

        return acc;
    }, {});

    return Object.entries(groups).map(([name, slots]) => ({ name, slots }));
};
</script>
